import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate, useSearchParams, useParams} from 'react-router-dom';
import {Card, Row, Col, ProgressBar, Badge} from 'react-bootstrap';
import {PageTitle} from '../../../../_metronic/layout/core';
import clsx from 'clsx';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import EditEmailAccountModal from './modals/EditEmailAccountModal';
import DeleteEmailAccountModal from './modals/DeleteEmailAccountModal';
import AddEmailAccountModal from './modals/AddEmailAccountModal';
import AddAliasModal from './modals/AddAliasModal';
import DeleteAliasModal from './modals/DeleteAliasModal';
import styles from '../../Styles/Email-accounts/DomaineEmailAccounts.module.scss';
import OutOfOfficeModal from './modals/OutOfOfficeModal';
import EmailAccountsTableCard from './../../Components/EmailAccounts/EmailAccountsTableCard';
import {getQuotaPercentage, getQuotaVariant, getTransportTypeLabel} from '../../Helpers/EmailAccounts/quotaUtils';
import {getMailboxInfo, createAlias, createMailboxAccount, deleteMailboxAccount, updateMailboxAccount, deleteAlias, createOrUpdateVacationMessage} from '../../Api/domainEmailAccounts';
import { useGetServiceInfo } from '../../Hooks/ServiceManager';

export interface Alias {
  account: string;
  active: boolean;
  alias: string;
}
export interface VacationMessage {
  email: string;
  subject: string;
  body: string;
  active: boolean;
}
export interface EmailAccount {
  id?: number;
  username: string;
  quota: number;
  quotaUsage: number | null;
  name: string;
  active: boolean;
  aliases: Alias[];
  vacationMessage: VacationMessage | null;
}
export interface MailboxData {
  nbMailBoxes: number;
  nbMaxMailBoxes: number;
  transportType: string;
  isActive: boolean;
  type?: string;
}



const DomaineEmailAccounts = (): JSX.Element => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {domainName} = useParams<{domainName: string}>();
  const serviceId: string = searchParams.get('serviceId') ?? '';

  const { data: dataService, isLoading: loadingService } = useGetServiceInfo(serviceId);

  // Modals
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddAliasModal, setShowAddAliasModal] = useState(false);
  const [showDeleteAliasModal, setShowDeleteAliasModal] = useState(false);
  const [showOutOfOfficeModal, setShowOutOfOfficeModal] = useState(false);
  const [accountForOutOfOffice, setAccountForOutOfOffice] = useState<EmailAccount | null>(null);

  const [selectedAccount, setSelectedAccount] = useState<EmailAccount | null>(null);
  const [accountForAlias, setAccountForAlias] = useState<EmailAccount | null>(null);
  const [aliasToDelete, setAliasToDelete] = useState<string | null>(null);

  const [mailboxData, setMailboxData] = useState<MailboxData | null>(null);
  const [emailAccounts, setEmailAccounts] = useState<EmailAccount[]>([]);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);

  const buName = searchParams.get('buId') || 'N/A';
  const siteName = domainName || searchParams.get('link_name')?.toLowerCase() || 'N/A';
  const serviceName = searchParams.get('service') || 'N/A';

  useEffect(() => {
    const fetchMailboxInfo = async () => {
      setIsLoadingAccounts(true);
      try {
        const data = await getMailboxInfo(domainName || siteName);
        setMailboxData({
          nbMailBoxes: data.domain.nbMailBoxes,
          nbMaxMailBoxes: data.domain.nbMaxMailBoxes,
          transportType: data.domain.transportType,
          isActive: data.domain.isActive,
          type: data.domain.type
        });

        setEmailAccounts(data.domain.accounts || []);
      } catch (error) {
        console.error('Erreur lors de la récupération des infos mailbox:', error);
        console.error('Domain utilisé:', domainName || siteName);
      } finally {
        setIsLoadingAccounts(false);
      }
    };

    if (domainName || siteName) {
      fetchMailboxInfo();
    }
  }, [domainName, siteName]);



  const handleManageLists = () => {
    navigate(`/services/domain/${siteName}/mailing-lists${location.search}`, {
      state: {
        ...((location.state as object) || {}),
        buName,
        siteName,
        serviceName,
        transportType: mailboxData?.transportType || 'N/A',
        isActive: mailboxData?.isActive || false,
        nbMailBoxes: mailboxData?.nbMailBoxes || 0,
        nbMaxMailBoxes: mailboxData?.nbMaxMailBoxes || 0,
      },
    });
  };


  const handleEdit = (account: EmailAccount) => {
    setSelectedAccount(account);
    setShowEditModal(true);
  };


  const handleSave = async (updatedAccount: EmailAccount, password?: string) => {
    try {
      if (!updatedAccount.id) {
        throw new Error('ID du compte manquant');
      }

      const accountLocal = updatedAccount.username.split('@')[0];

      // Si un mot de passe est fourni, on l'utilise, sinon on envoie null
      const passwordToSend = password && password.trim() ? password : null;

      // Mise à jour des informations du compte (nom, quota, statut et mot de passe si fourni)
      await updateMailboxAccount(
        domainName || siteName,
        updatedAccount.id,
        {
          account: accountLocal,
          password: passwordToSend,
          name: updatedAccount.name,
          quota: updatedAccount.quota,
          active: updatedAccount.active
        }
      );

      // Rafraîchir les données depuis le serveur
      const data = await getMailboxInfo(domainName || siteName);
      setEmailAccounts(data.domain.accounts || []);

      console.log('Compte modifié avec succès:', updatedAccount);
    } catch (error) {
      console.error('Erreur lors de la modification du compte:', error);
      throw error;
    }
  };


  const handleDeleteClick = (account: EmailAccount) => {
    setSelectedAccount(account);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedAccount) {
      try {
        // Utilisation de l'ID si disponible, sinon erreur
        if (!selectedAccount.id) {
          throw new Error('ID du compte manquant');
        }

        await deleteMailboxAccount(domainName || siteName, selectedAccount.id);

        // Mise à jour de l'état local après succès
        setEmailAccounts((prev) => prev.filter((acc) => acc.username !== selectedAccount.username));

        console.log('Compte supprimé:', selectedAccount);
      } catch (error) {
        console.error('Erreur lors de la suppression du compte:', error);
        throw error; // Propagation de l'erreur pour que le modal puisse la gérer
      }
    }
  };


  const handleAdd = async (newAccount: {
    account: string;
    password: string;
    name: string;
    quota: number;
  }) => {
    try {
      await createMailboxAccount(domainName || siteName, {
        account: newAccount.account,
        password: newAccount.password,
        name: newAccount.name,
        quota: newAccount.quota
      });

      // Refresh list after adding
      const data = await getMailboxInfo(domainName || siteName);

      // update email accounts list
      setEmailAccounts(data.domain.accounts || []);

      // update mailbox data
      setMailboxData({
        nbMailBoxes: data.domain.nbMailBoxes,
        nbMaxMailBoxes: data.domain.nbMaxMailBoxes,
        transportType: data.domain.transportType,
        isActive: data.domain.isActive,
      });

      console.log('Compte ajouté avec succès et liste actualisée');
    } catch (error) {
      console.error('Erreur lors de la création du compte:', error);
      throw error;
    }
  };

  const handleAddAliasClick = (account: EmailAccount) => {
    setAccountForAlias(account);
    setShowAddAliasModal(true);
  };

  const handleAddAlias = async (aliasLocal: string) => {
    if (accountForAlias) {
      try {
        await createAlias(domainName || siteName, {
          alias: aliasLocal,
          account: accountForAlias.username
        });

        // refresh list after adding alias
        const data = await getMailboxInfo(domainName || siteName);
        setEmailAccounts(data.domain.accounts || []);

        console.log('Alias ajouté:', aliasLocal, 'pour le compte:', accountForAlias.username);
      } catch (error) {
        console.error('Erreur lors de l\'ajout de l\'alias:', error);
        throw error;
      }
    }
  };

  const handleDeleteAliasClick = (account: EmailAccount, alias: string) => {

    setAccountForAlias(account);
    setAliasToDelete(alias);
    setShowDeleteAliasModal(true);
  };

  const handleDeleteAliasConfirm = async () => {
    if (accountForAlias && aliasToDelete) {
      try {
        await deleteAlias(domainName || siteName,accountForAlias.id ?? 0,  aliasToDelete);

        setEmailAccounts((prev) =>
          prev.map((acc) =>
            acc.username === accountForAlias.username
              ? {...acc, aliases: acc.aliases.filter((a) => a.alias !== aliasToDelete)}
              : acc
          )
        );

      } catch (error) {
        throw error; // Propagation de l'erreur pour que le modal puisse la gérer
      }
    }
  };

  const handleOutOfOfficeClick = (account: EmailAccount) => {
    setAccountForOutOfOffice(account);
    setShowOutOfOfficeModal(true);
  };

  const handleSaveOutOfOffice = async (vacationData: {
    subject: string;
    body: string;
    email: string;
    active: boolean;
  }) => {
    if (accountForOutOfOffice) {
      try {
        // Vérification de l'ID
        if (!accountForOutOfOffice.id) {
          throw new Error('ID du compte manquant');
        }

        // Appel API pour sauvegarder le message d'absence
        await createOrUpdateVacationMessage(
          domainName || siteName,
          accountForOutOfOffice.id,
          {
            subject: vacationData.subject,
            body: vacationData.body,
            active: vacationData.active,
          }
        );

        // Rafraîchir les données depuis le serveur pour avoir les données à jour
        const data = await getMailboxInfo(domainName || siteName);
        setEmailAccounts(data.domain.accounts || []);

        console.log('Message d\'absence modifié pour:', accountForOutOfOffice.username, vacationData);
      } catch (error) {
        console.error('Erreur lors de la modification du message d\'absence:', error);
        throw error; // Propagation de l'erreur pour que le modal puisse la gérer
      }
    }
  };


  return (
    <>
      <PageTitle breadcrumbs={[]}>{`${buName} - ${siteName}`}</PageTitle>
      <div className={clsx(styles.containerDomainEmailAccounts)}>
        <Row className="g-4 mb-5">
          <Col md={6}>
            <Card>
              <Card.Header>
                <Card.Title>
                  <Level3Title title="Informations générales" />
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="info-row">
                  <span className="info-label">BU :</span>
                  <span className="info-value">{buName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Site :</span>
                  <span className="info-value">{siteName}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Service :</span>
                  <span className="info-value">
                    {loadingService ? (
                      <span className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="text-muted">Chargement...</span>
                      </span>
                    ) : (
                      <span>{dataService?.serviceId + ' ' + dataService?.serviceName}</span>
                    )}
                  </span>
                </div>


              </Card.Body>
            </Card>
          </Col>

          <Col md={6}>
            <Card>
              <Card.Header>
                <Card.Title>
                  <Level3Title title="Comptes de messagerie existants" />
                </Card.Title>
              </Card.Header>
              <Card.Body>
                <div className="info-row">
                  <span className="info-label">Relais de messagerie :</span>
                  <span className="info-value">
                    {mailboxData ? (
                      getTransportTypeLabel(mailboxData)
                    ) : (
                      <span className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="text-muted">Chargement...</span>
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">État :</span>
                  <span className="info-value">
                    {mailboxData ? (
                      <Badge
                        bg={mailboxData.isActive ? 'success' : 'secondary'}
                        className="text-capitalize"
                      >
                        {mailboxData.isActive ? 'Actif' : 'Inactif'}
                      </Badge>
                    ) : (
                      <span className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="text-muted">Chargement...</span>
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Nombre de comptes :</span>
                  <span className="info-value">
                    {mailboxData ? (
                      mailboxData.nbMailBoxes
                    ) : (
                      <span className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="text-muted">Chargement...</span>
                      </span>
                    )}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Quota :</span>
                  <div className="info-value" style={{flex: 1, minWidth: 0, maxWidth: '210px'}}>
                    {mailboxData ? (
                      mailboxData.nbMaxMailBoxes === 0 ? (
                        // Quota illimité
                        <div className="d-flex justify-content-end">
                          <span>illimité</span>
                        </div>
                      ) : (
                        // Quota limité avec barre de progression
                        <>
                          <div className="d-flex justify-content-between mb-1">
                            <small className="text-muted">
                              {mailboxData.nbMailBoxes} / {mailboxData.nbMaxMailBoxes}
                            </small>
                            <small className="fw-bold">
                              {getQuotaPercentage(mailboxData.nbMailBoxes, mailboxData.nbMaxMailBoxes)}%
                            </small>
                          </div>
                          <div style={{
                            border: '1px solid #e0e0e0',
                            borderRadius: '6px',
                            overflow: 'hidden',
                            backgroundColor: '#f8f9fc'
                          }}>
                            <ProgressBar
                              now={getQuotaPercentage(mailboxData.nbMailBoxes, mailboxData.nbMaxMailBoxes)}
                              variant={getQuotaVariant(getQuotaPercentage(mailboxData.nbMailBoxes, mailboxData.nbMaxMailBoxes))}
                              style={{height: '10px', borderRadius: '0'}}
                            />
                          </div>
                        </>
                      )
                    ) : (
                      // Fallback pendant le chargement
                      <span className="d-flex align-items-center gap-2 justify-content-end">
                        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                        <span className="text-muted">Chargement...</span>
                      </span>
                    )}
                  </div>
                </div>
              </Card.Body>


            </Card>
          </Col>
        </Row>

        <EmailAccountsTableCard
          emailAccounts={emailAccounts}
          onAddAccount={() => setShowAddModal(true)}
          onEditAccount={handleEdit}
          onDeleteAccount={handleDeleteClick}
          onAddAlias={handleAddAliasClick}
          onDeleteAlias={handleDeleteAliasClick}
          onManageLists={handleManageLists}
          onManageOutOfOffice={handleOutOfOfficeClick}
          isLoading={isLoadingAccounts}
          domainType ={mailboxData?.type || ''}
        />



        {/* Modal d'ajout de compte */}
        <AddEmailAccountModal
          show={showAddModal}
          onHide={() => setShowAddModal(false)}
          onAdd={handleAdd}
          domainName={siteName}
        />

        {/* Modal de modification de compte */}
        <EditEmailAccountModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          account={selectedAccount}
          onSave={handleSave}
        />

        {/* Modal de suppression de compte */}
        <DeleteEmailAccountModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          account={selectedAccount}
          onConfirm={handleDeleteConfirm}
        />

        {/* Modal d'ajout d'alias */}
        <AddAliasModal
          show={showAddAliasModal}
          onHide={() => setShowAddAliasModal(false)}
          onAdd={handleAddAlias}
          accountEmail={accountForAlias?.username || ''}
          domainName={siteName}
        />

        {/* Modal de suppression d'alias */}
        <DeleteAliasModal
          show={showDeleteAliasModal}
          onHide={() => setShowDeleteAliasModal(false)}
          onConfirm={handleDeleteAliasConfirm}
          alias={aliasToDelete}
          accountEmail={accountForAlias?.username || ''}
        />

        {/* Modal de message d'absence */}
        <OutOfOfficeModal
          show={showOutOfOfficeModal}
          onHide={() => setShowOutOfOfficeModal(false)}
          accountEmail={accountForOutOfOffice?.username || ''}
          messageAbsence={accountForOutOfOffice?.vacationMessage}
          onSave={handleSaveOutOfOffice}
        />


      </div>
    </>
  );
};

export default DomaineEmailAccounts;
